/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import classes from './tickets.module.css';
import Skeleton from '../../../components/skeleton/skeleton';
import { FaBan, FaEye, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { format } from 'date-fns';

const url = process.env.REACT_APP_BACKEND_URL;

export default function Users() {
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    const [otherData, setOtherData] = useState({})
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState(''); // 'expert', 'revoke', 'delete'
    const [selectedUser, setSelectedUser] = useState(null);
    const [expertSummary, setExpertSummary] = useState(null);
    const [expertPayouts, setExpertPayouts] = useState([]);
    const [expertPayoutsPagination, setExpertPayoutsPagination] = useState({ page: 1, pages: 1, total: 0 });
    const [isExpertSummaryLoading, setIsExpertSummaryLoading] = useState(false);
    const [isExpertPayoutsLoading, setIsExpertPayoutsLoading] = useState(false);
    const [isProcessingPayout, setIsProcessingPayout] = useState(false);


    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const fetchUsers = async (page = 1) => {

        const config = {
            headers: {
                authorization: `Bearer ${localStorage.getItem('tok')}`
            }
        }

        setIsLoading(true);

        try {
            const dateParams = `${startDate ? `&startDate=${startDate}` : ''}${endDate ? `&endDate=${endDate}` : ''}`;
            const sortParams = `&sortBy=${sortBy}&sortOrder=${sortOrder}`;
            const response = await axios.get(`${url}/users/all?searchQuery=${searchQuery}&page=${page}&vip=${activeTab !== 'all' ? 'true' : ''}${dateParams}${sortParams}`, config);
            setUsers(response.data.users);
            const { users, ...otherData } = response.data;
            setOtherData(otherData);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const { totalCount, hasNextPage, hasPreviousPage, page } = otherData;

    useEffect(() => {
        const handler = setTimeout(() => {
            setSearchQuery(searchTerm);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    useEffect(() => {
        fetchUsers(1);
    }, [searchQuery, activeTab, startDate, endDate, sortBy, sortOrder]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSortChange = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder(field === 'referralsCount' ? 'desc' : 'asc');
        }
    };

    const handleClearDateFilter = () => {
        setStartDate('');
        setEndDate('');
    };

    const handleToggleExpert = (user) => {
        setSelectedUser(user);
        setModalType(user.expert ? 'revoke' : 'expert');
        setIsModalOpen(true);
    };

    const confirmToggleExpert = async () => {
        const config = {
            headers: {
                authorization: `Bearer ${localStorage.getItem('tok')}`
            }
        }
        try {
            await axios.patch(`${url}/users/toggle-expert`, { userId: selectedUser._id, action: !selectedUser.expert }, config);
            fetchUsers(page);
            setIsModalOpen(false);
        } catch (error) {
            console.error('Error updating expert status:', error);
        }
    };

    const handleDeleteUser = (user) => {
        setSelectedUser(user);
        setModalType('delete');
        setIsModalOpen(true);
    }

    const confirmDeleteUser = async () => {
        const id = selectedUser._id;
        const config = {
            headers: {
                authorization: `Bearer ${localStorage.getItem('tok')}`
            }
        }
        try {
            await axios.delete(`${url}/users/${id}`, config);
            fetchUsers(page);
            setIsModalOpen(false);
        } catch (error) {
            console.error('Error deleting user:', error);
        }
    }

    const fetchExpertSummary = async (user) => {
        const config = {
            headers: {
                authorization: `Bearer ${localStorage.getItem('tok')}`
            }
        };

        setIsExpertSummaryLoading(true);
        try {
            const response = await axios.get(`${url}/payment/expert-summary?userId=${user._id}`, config);
            setExpertSummary(response.data);
        } catch (error) {
            console.error('Error fetching expert summary:', error);
        } finally {
            setIsExpertSummaryLoading(false);
        }
    };

    const fetchExpertPayouts = async (user, p = 1) => {
        const config = {
            headers: {
                authorization: `Bearer ${localStorage.getItem('tok')}`
            }
        };

        setIsExpertPayoutsLoading(true);
        try {
            const response = await axios.get(`${url}/payment/payouts?userId=${user._id}&page=${p}`, config);
            setExpertPayouts(response.data.payouts);
            setExpertPayoutsPagination(response.data.pagination);
        } catch (error) {
            console.error('Error fetching expert payouts:', error);
        } finally {
            setIsExpertPayoutsLoading(false);
        }
    };

    const handleViewExpertDetails = async (user) => {
        setSelectedUser(user);
        setModalType('details');
        setIsModalOpen(true);
        setExpertSummary(null);
        setExpertPayouts([]);
        setExpertPayoutsPagination({ page: 1, pages: 1, total: 0 });

        fetchExpertSummary(user);
        fetchExpertPayouts(user, 1);
    };

    const handlePayExpert = async () => {
        if (!selectedUser || !expertSummary || expertSummary.totalAvailable <= 0) return;

        setIsProcessingPayout(true);
        const config = {
            headers: {
                authorization: `Bearer ${localStorage.getItem('tok')}`
            }
        };

        try {
            await axios.post(`${url}/payment/pay-expert`, { userId: selectedUser._id }, config);
            // Refresh details
            fetchExpertSummary(selectedUser);
            fetchExpertPayouts(selectedUser, 1);
            setModalType('details');
        } catch (error) {
            console.error('Error paying expert:', error);
            alert('Failed to process payout');
        } finally {
            setIsProcessingPayout(false);
        }
    };

    const executeAction = () => {
        if (modalType === 'delete') {
            confirmDeleteUser();
        } else if (modalType === 'details') {
            setIsModalOpen(false);
        } else {
            confirmToggleExpert();
        }
    };

    const formatCurrency = (amount) => {
        return `₦${Number(amount || 0).toLocaleString()}`;
    };

    const renderSortIcon = (field) => {
        if (sortBy !== field) return <FaSort className={classes.sortIconInactive} />;
        return sortOrder === 'asc' ? <FaSortUp className={classes.sortIconActive} /> : <FaSortDown className={classes.sortIconActive} />;
    };

    return (
        <div className={classes.container}>
            <div className={classes.subContainer}>
                <div className={classes.tabs}>
                    <button onClick={() => handleTabChange('all')} className={activeTab === 'all' ? classes.active : ''}>All</button>
                    <button onClick={() => handleTabChange('vip')} className={activeTab === 'vip' ? classes.active : ''}>VIP</button>
                </div>
                <div className={classes.filterRow}>
                    <div className={classes.inputContainer}>
                        <input
                            type="text"
                            placeholder="Search by username or email..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className={classes.input}
                        />
                    </div>
                    <div className={classes.dateFilterContainer}>
                        <div className={classes.dateInputGroup}>
                            <label className={classes.dateLabel}>Sort By</label>
                            <select
                                value={`${sortBy}-${sortOrder}`}
                                onChange={(e) => {
                                    const [newSortBy, newSortOrder] = e.target.value.split('-');
                                    setSortBy(newSortBy);
                                    setSortOrder(newSortOrder);
                                }}
                                className={classes.sortSelect}
                            >
                                <option value="name-asc">Name (A - Z)</option>
                                <option value="name-desc">Name (Z - A)</option>
                                <option value="referralsCount-desc">Ref. Count (High to Low)</option>
                                <option value="referralsCount-asc">Ref. Count (Low to High)</option>
                                <option value="createdAt-desc">Newest First</option>
                                <option value="createdAt-asc">Oldest First</option>
                            </select>
                        </div>
                        <div className={classes.dateInputGroup}>
                            <label className={classes.dateLabel}>Start Date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className={classes.dateInput}
                            />
                        </div>
                        <div className={classes.dateInputGroup}>
                            <label className={classes.dateLabel}>End Date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className={classes.dateInput}
                            />
                        </div>
                        {(startDate || endDate) && (
                            <button
                                type="button"
                                onClick={handleClearDateFilter}
                                className={classes.clearDateBtn}
                            >
                                Clear Dates
                            </button>
                        )}
                    </div>
                </div>

                <p className={classes.count}>Count: {totalCount}</p>

                {isLoading ?
                    <div className={classes.loaderContainer}>
                        <Skeleton width={'100%'} height={'100%'} />
                    </div>
                    :
                    <>
                        <table className={classes.table}>
                            <thead>
                                <tr>
                                    <th>S/N</th>
                                    <th onClick={() => handleSortChange('name')} className={classes.sortableHeader}>
                                        Name {renderSortIcon('name')}
                                    </th>
                                    <th>Email</th>
                                    <th onClick={() => handleSortChange('createdAt')} className={classes.sortableHeader}>
                                        Joined Date {renderSortIcon('createdAt')}
                                    </th>
                                    <th onClick={() => handleSortChange('referralsCount')} className={classes.sortableHeader}>
                                        Ref. Count {renderSortIcon('referralsCount')}
                                    </th>
                                    <th>Expert</th>
                                    <th>Ban</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user, index) => (
                                    <tr key={user._id} className={user.expert ? classes.expertRow : ''}>
                                        <td>{index + 1}</td>
                                        <td
                                            className={user.expert ? classes.clickableName : ''}
                                            onClick={() => user.expert && handleViewExpertDetails(user)}
                                        >
                                            {`${user.first_name} ${user.last_name}`}
                                            {user.expert && <FaEye className={classes.viewIcon} title="View Expert Details" />}
                                        </td>
                                        <td>{user.email}</td>
                                        <td>{user.createdAt ? format(new Date(user.createdAt), 'MMM dd, yyyy') : 'N/A'}</td>
                                        <td>{user?.referralsCount || 0}</td>
                                        <td>
                                            <button
                                                className={`${classes.expertBtn} ${user.expert ? classes.expertActive : ''}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleToggleExpert(user);
                                                }}
                                            >
                                                {user.expert ? 'Revoke Expert' : 'Make Expert'}
                                            </button>
                                        </td>
                                        <td>
                                            <button className={classes.buttonContainer} onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteUser(user);
                                            }}>
                                                <FaBan className={classes.deleteBtn} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className={classes.pagination}>
                            <button onClick={() => fetchUsers(parseInt(page) - 1)} disabled={!hasPreviousPage}>Previous</button>
                            <span>Page {page} </span>
                            <button onClick={() => fetchUsers(parseInt(page) + 1)} disabled={!hasNextPage}>Next</button>
                        </div>
                    </>
                }
            </div>

            {isModalOpen && (
                <div className={classes.overlay}>
                    <div className={`${classes.overlayCard} ${modalType === 'details' ? classes.detailsCard : ''}`}>
                        <h3 className={classes.editTitle}>
                            {modalType === 'details' ? 'Expert Performance' : 'Confirm Action'}
                        </h3>

                        {modalType === 'details' ? (
                            <div className={classes.detailsContent}>
                                <div className={classes.userHeader}>
                                    <p className={classes.userName}>{selectedUser?.first_name} {selectedUser?.last_name}</p>
                                    <p className={classes.userEmail}>{selectedUser?.email}</p>
                                </div>

                                {isExpertSummaryLoading ? (
                                    <div className={classes.modalLoading}>
                                        <Skeleton width={'100%'} height={'100px'} />
                                    </div>
                                ) : expertSummary && (
                                    <>
                                        <div className={`${classes.modalStatCard} ${classes.available}`}>
                                            <span>Amount accumulated after last payout</span>
                                            <p>{formatCurrency(expertSummary.totalAvailable)}</p>
                                        </div>

                                        <div className={classes.modalStatsGrid}>

                                            <div className={`${classes.modalStatCard} ${classes.earnings}`}>
                                                <span>Overall Earned From Best Codes</span>
                                                <p>{formatCurrency(expertSummary.amountMade)}</p>
                                            </div>
                                            <div className={`${classes.modalStatCard} ${classes.settled}`}>
                                                <span>Total Money Paid To Expert</span>
                                                <p>{formatCurrency(expertSummary.totalSettlement)}</p>
                                            </div>
                                            <div className={`${classes.modalStatCard} ${classes.referrals}`}>
                                                <span>How Many Referrals Used Your Code</span>
                                                <p>{expertSummary.totalReferrals}</p>
                                            </div>
                                            <div className={`${classes.modalStatCard} ${classes.referrals}`}>
                                                <span>How Many Times Referrals Subscribed</span>
                                                <p>{expertSummary.referralsThatPaid}</p>
                                            </div>
                                        </div>

                                        <div className={classes.payoutSection}>
                                            <div className={classes.payoutHeader}>
                                                <h4>Recent Payouts ({expertPayoutsPagination.total})</h4>
                                                <button
                                                    className={classes.payBtn}
                                                    onClick={() => setModalType('pay_confirm')}
                                                    disabled={expertSummary.totalAvailable <= 0 || isProcessingPayout}
                                                >
                                                    {isProcessingPayout ? 'Processing...' : 'Pay Expert'}
                                                </button>
                                            </div>

                                            <div className={classes.payoutTableContainer} style={{ opacity: isExpertPayoutsLoading ? 0.6 : 1 }}>
                                                {expertPayouts.length > 0 ? (
                                                    <>
                                                        <table className={classes.miniTable}>
                                                            <thead>
                                                                <tr>
                                                                    <th>Date</th>
                                                                    <th>Amount</th>
                                                                    <th>Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {expertPayouts.map((p, i) => (
                                                                    <tr key={i}>
                                                                        <td>{format(new Date(p.createdAt), 'MMM dd, yyyy')}</td>
                                                                        <td>{formatCurrency(p.amount)}</td>
                                                                        <td className={classes.statusPaid}>{p.status}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        {expertPayoutsPagination.pages > 1 && (
                                                            <div className={classes.pagination} style={{ marginTop: '10px' }}>
                                                                <button
                                                                    style={{ padding: '5px 10px', fontSize: '12px' }}
                                                                    onClick={() => fetchExpertPayouts(selectedUser, expertPayoutsPagination.page - 1)}
                                                                    disabled={expertPayoutsPagination.page === 1 || isExpertPayoutsLoading}
                                                                >
                                                                    Prev
                                                                </button>
                                                                <span style={{ fontSize: '12px' }}>{expertPayoutsPagination.page} / {expertPayoutsPagination.pages}</span>
                                                                <button
                                                                    style={{ padding: '5px 10px', fontSize: '12px' }}
                                                                    onClick={() => fetchExpertPayouts(selectedUser, expertPayoutsPagination.page + 1)}
                                                                    disabled={expertPayoutsPagination.page === expertPayoutsPagination.pages || isExpertPayoutsLoading}
                                                                >
                                                                    Next
                                                                </button>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <p className={classes.noData}>No payout history found.</p>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}

                                    <div className={classes.modalActions}>
                                        <button className={classes.confirmBtn} onClick={() => setIsModalOpen(false)}>Close</button>
                                    </div>
                                </div>
                            ) : modalType === 'pay_confirm' ? (
                                        <>
                                            <p style={{ textAlign: 'center', marginTop: '10px' }}>
                                                Are you sure you want to pay <b>{selectedUser?.first_name} {selectedUser?.last_name}</b> the sum of <b>{formatCurrency(expertSummary?.totalAvailable)}</b>?
                                            </p>
                                            <div className={classes.modalActions}>
                                                <button className={classes.cancelBtn} onClick={() => setModalType('details')}>Cancel</button>
                                                <button className={classes.confirmBtn} onClick={handlePayExpert} disabled={isProcessingPayout}>
                                                    {isProcessingPayout ? 'Processing...' : 'Confirm Payout'}
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <p style={{ textAlign: 'center', marginTop: '10px' }}>
                                                Are you sure you want to {
                                                    modalType === 'delete' ? 'delete/ban' :
                                                        modalType === 'revoke' ? 'revoke expert status for' :
                                                            'make expert'
                                                } <b>{selectedUser?.first_name} {selectedUser?.last_name}</b>?
                                            </p>
                                            <div className={classes.modalActions}>
                                                <button className={classes.cancelBtn} onClick={() => setIsModalOpen(false)}>Cancel</button>
                                                <button className={classes.confirmBtn} onClick={executeAction}>Confirm</button>
                                            </div>
                                        </>
                                    )}
                    </div>
                </div>
            )}
        </div>
    );
}
